'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@savage/supabase/client';
import { Heading, Text, Divider, Button, Link as EditorialLink } from '@savage-ds/primitives';
import { Field, Input } from '@savage-ds/forms';
import { Alert, Spinner } from '@savage-ds/feedback';
import { AuthProviderButton, AuthProviderGroup } from '@savage-ds/shell';
import { GoogleIcon, MicrosoftIcon, EmailIcon } from '@savage-ds/brand';
import { reportingReturnDestination } from '@/lib/auth/return-destination';
import { authCookieNames, authProofDiagnostics } from '@/lib/auth/proof-diagnostics';

// The app runs under the `/reporting` basePath. `router.push` auto-prepends
// basePath, so client-side navigation uses the un-prefixed path. OAuth /
// recovery redirects are built with `new URL(path, origin)`, which does NOT
// get basePath applied, so those carry the explicit `/reporting` prefix — this
// preserves the requested report and filters without doubling the basePath.
const CALLBACK_URL = '/reporting/auth/callback';

function requestedDestination() {
  const values = new URLSearchParams(window.location.search).getAll('next');
  return reportingReturnDestination(values.length === 1 ? values[0] : null);
}

/**
 * Reporting Maximizer sign-in. Google (offline access + consent) and Microsoft
 * both go through the client Supabase OAuth transport, matching the retired
 * @savage/auth-ui behavior for this app (no server-side start route). Email /
 * password uses the same client transport.
 */
export default function LoginForm() {
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  function showError(msg: string) {
    setError(msg);
    setSuccess('');
    setLoading(false);
  }

  async function handleOAuth(provider: 'google' | 'azure') {
    setLoading(true);
    setError('');

    const observeProof = (stage: 'before-initialization' | 'after-initialization' | 'initialization-rejected' | 'oauth-returned', sessionError = false) => {
      try {
        console.info('[reporting-auth-proof]', {
          stage, sessionError,
          ...authProofDiagnostics(authCookieNames(document.cookie),
            process.env.NEXT_PUBLIC_SUPABASE_URL, process.env.NEXT_PUBLIC_AUTH_COOKIE_DOMAIN),
        });
      } catch { /* Diagnostics must not change the sign-in outcome. */ }
    };
    observeProof('before-initialization');
    const supabase = createClient();
    // Finish stale-session recovery before creating PKCE proof. Auth-js OAuth
    // start does not await initialization; failed refresh cleanup otherwise
    // removes the newly written verifier before the callback can exchange it.
    // An invalid old session is expected here: this is a fresh sign-in.
    try {
      const result = await supabase.auth.getSession();
      observeProof('after-initialization', Boolean(result.error));
    } catch {
      observeProof('initialization-rejected');
      showError('Unable to prepare sign-in. Please try again.');
      return;
    }
    const callbackUrl = new URL(CALLBACK_URL, window.location.origin);
    callbackUrl.searchParams.set('next', requestedDestination().callbackPath);

    const options: Record<string, unknown> = { redirectTo: callbackUrl.toString() };
    if (provider === 'google') {
      options.queryParams = { access_type: 'offline', prompt: 'consent' };
    }
    if (provider === 'azure') {
      options.scopes = 'email openid profile';
    }

    const { error: oauthError } = await supabase.auth.signInWithOAuth({ provider, options });
    // The SDK already initiated navigation. This is a best-effort observation,
    // not a navigation barrier or proof that the callback received the cookie.
    observeProof('oauth-returned', Boolean(oauthError));
    if (oauthError) {
      showError(oauthError.message || 'Sign-in failed. Please try again.');
    }
    // On success the browser navigates away; loading stays on intentionally.
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) { showError('Please enter your email address.'); return; }
    if (!password) { showError('Please enter your password.'); return; }

    setError('');
    setSuccess('');
    setLoading(true);

    const supabase = createClient();
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim(),
      password,
    });

    if (signInError) {
      showError(signInError.message || 'Invalid email or password.');
    } else {
      setSuccess('Welcome back! Redirecting…');
      router.push(requestedDestination().clientPath);
    }
  }

  return (
    <>
      <Heading level={1}>
        Sign in to <em>Reporting Maximizer</em>
      </Heading>
      <Text variant="muted" className="ds-auth-lede">
        Welcome back
      </Text>

      <AuthProviderGroup>
        <AuthProviderButton
          tone="solid"
          icon={<GoogleIcon />}
          disabled={loading}
          onClick={() => handleOAuth('google')}
        >
          Continue with Google
        </AuthProviderButton>

        <AuthProviderButton
          tone="outline"
          icon={<MicrosoftIcon />}
          disabled={loading}
          onClick={() => handleOAuth('azure')}
        >
          Continue with Microsoft
        </AuthProviderButton>

        {!showEmailForm && (
          <AuthProviderButton
            tone="ghost"
            icon={<EmailIcon />}
            disabled={loading}
            onClick={() => setShowEmailForm(true)}
          >
            Sign in with email
          </AuthProviderButton>
        )}
      </AuthProviderGroup>

      {(loading || error || success) && (
        <div className="ds-auth-status">
          {loading && <Spinner size="lg" label="Signing in" />}
          {error && <Alert tone="danger">{error}</Alert>}
          {success && <Alert tone="success">{success}</Alert>}
        </div>
      )}

      {showEmailForm && (
        <div className="ds-auth-manual">
          <Divider label="or sign in with email & password" />
          <form onSubmit={handleSubmit} noValidate>
            <Field label="Email address">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </Field>
            <Field label="Password">
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your password"
                autoComplete="current-password"
              />
            </Field>
            <Button type="submit" variant="primary" size="full" loading={loading} disabled={loading}>
              {loading ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
          <Text variant="small" className="ds-auth-alt">
            <EditorialLink href="/reporting/forgot-password">Forgot password?</EditorialLink>
          </Text>
        </div>
      )}

      <Text variant="small" className="ds-auth-alt">
        Need access? <EditorialLink href="/reporting/signup">Create an account</EditorialLink>
      </Text>
    </>
  );
}
